import {
  safetyBadges,
  sabbatStart,
  type CrystalCare,
  type OilSafety,
  type PlantSafety,
  type RecipeSafety,
} from "@hexling/core";
import type { ContentCategory, ContentItem } from "@hexling/schema";
import { EmptyState, SafetyLabel, Spinner } from "@hexling/ui";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { ItemActions } from "../../components/ItemActions";
import { PageTitle } from "../../components/Layout";
import type { ContentIndex } from "../../data/content";
import { useContent, useServices, useSettings } from "../../data/AppData";
import { eventDate, formatDay } from "../../lib/dates";
import { categoryInfo } from "./categories";

type Data = Record<string, unknown>;
const words = (v: unknown) =>
  Array.isArray(v) ? (v as unknown[]).map((x) => String(x).replace(/_/g, " ")) : [];
const text = (v: unknown) => (typeof v === "string" ? v : typeof v === "number" ? String(v) : "");
const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

export function ItemPage({ category, slug }: { category: ContentCategory; slug: string }) {
  const content = useContent();
  if (content.isPending) return <Spinner />;
  const index = content.data;
  const item = index?.get(category, slug);
  const info = categoryInfo(category);
  if (!index || !item) {
    return (
      <EmptyState
        title="We couldn't find that entry"
        action={<Link to="/library">Back to the library</Link>}
      >
        It may have been renamed or removed.
      </EmptyState>
    );
  }
  const latin = text(item.data.latin_name);

  return (
    <article className="plate">
      <PageTitle
        title={item.name}
        subtitle={latin ? <span className="hx-latin">{latin}</span> : undefined}
        back={{ to: "/library/$category", label: info?.label ?? "Library", params: { category } }}
      />
      <ItemActions item={{ category, slug, name: item.name }} />
      <Body item={item} index={index} />
      <NotesAbout category={category} slug={slug} />
    </article>
  );
}

function Body({ item, index }: { item: ContentItem; index: ContentIndex }) {
  const d = item.data;
  switch (item.category) {
    case "plant":
      return (
        <>
          <p className="plate__lede">{text(d.description)}</p>
          <SafetyLabel
            badges={safetyBadges({ category: "plant", safety: d.safety as PlantSafety })}
            cautions={(d.safety as PlantSafety).human_cautions}
            footnote={petFootnote(d.safety as PlantSafety)}
          />
          <Facts
            rows={[
              ["Also called", words(d.other_names).join(", ")],
              ["Element", cap(text(d.element))],
              ["Planet", cap(text(d.planet))],
            ]}
          />
          <Intents intents={d.intents} index={index} />
        </>
      );
    case "crystal": {
      const care = d.care as CrystalCare & { recommended_cleansing: string[] };
      return (
        <>
          <p className="plate__lede">{text(d.description)}</p>
          <SafetyLabel
            badges={safetyBadges({ category: "crystal", care })}
            footnote={`Cleanse with: ${words(care.recommended_cleansing).join(", ")}.`}
          />
          <Facts
            rows={[
              ["Colors", words(d.colors).join(", ")],
              ["Hardness", d.mohs_hardness ? `${text(d.mohs_hardness)} on the Mohs scale` : ""],
              ["Composition", text(d.chemical_composition)],
              ["Chakras", words(d.chakras).join(", ")],
              ["Signs", words(d.zodiac).map(cap).join(", ")],
              ["Element", cap(text(d.element))],
              ["Planet", cap(text(d.planet))],
            ]}
          />
          <Intents intents={d.intents} index={index} />
        </>
      );
    }
    case "oil": {
      const s = d.safety as OilSafety & { max_topical_dilution_pct?: number };
      const plant =
        typeof d.source_plant_slug === "string"
          ? index.get("plant", d.source_plant_slug)
          : undefined;
      return (
        <>
          <p className="plate__lede">{text(d.description)}</p>
          {d.oil_type === "essential" ? (
            <SafetyLabel
              badges={safetyBadges({ category: "oil", safety: s })}
              cautions={s.human_cautions}
              footnote={
                s.max_topical_dilution_pct !== undefined
                  ? `Never use undiluted. Keep skin blends at or below ${s.max_topical_dilution_pct}% in a carrier oil.`
                  : undefined
              }
            />
          ) : (
            <SafetyLabel badges={[]} cautions={s.human_cautions} />
          )}
          <Facts
            rows={[
              ["Type", d.oil_type === "essential" ? "Essential oil" : "Carrier oil"],
              ["Element", cap(text(d.element))],
              ["Planet", cap(text(d.planet))],
            ]}
          />
          {plant && (
            <p>
              Distilled from{" "}
              <Link to="/library/$category/$slug" params={{ category: "plant", slug: plant.slug }}>
                {plant.name}
              </Link>
              .
            </p>
          )}
          <Intents intents={d.intents} index={index} />
        </>
      );
    }
    case "recipe":
      return <Recipe item={item} index={index} />;
    case "tarot_card":
      return (
        <>
          <Facts
            rows={[
              ["Arcana", cap(text(d.arcana))],
              ["Suit", cap(text(d.suit))],
              ["Element", text(d.element)],
              ["Planet", text(d.planet)],
              ["Sign", text(d.zodiac)],
            ]}
          />
          <section className="tarot-sides">
            <div>
              <h2>Upright</h2>
              <p className="hx-muted">{words(d.keywords_upright).join(", ")}</p>
              <p>{text(d.meaning_upright)}</p>
            </div>
            <div>
              <h2>Reversed</h2>
              <p className="hx-muted">{words(d.keywords_reversed).join(", ")}</p>
              <p>{text(d.meaning_reversed)}</p>
            </div>
          </section>
          <Facts
            rows={[
              ["Love", text(d.love)],
              ["Work", text(d.career)],
              ["Spirit", text(d.spiritual)],
              ["Mood", text(d.mood)],
            ]}
          />
        </>
      );
    case "sabbat":
      return <Sabbat item={item} index={index} />;
    default:
      return <Generic data={d} index={index} summary={item.summary} />;
  }
}

function petFootnote(s: PlantSafety): string {
  const label = { toxic: "toxic", non_toxic: "non-toxic", not_listed: "not listed" } as const;
  const base = `Cats: ${label[s.pets.cats]}. Dogs: ${label[s.pets.dogs]}. Horses: ${label[s.pets.horses]}. Source: ASPCA.`;
  return s.pets.cats === "not_listed" || s.pets.dogs === "not_listed"
    ? `${base} "Not listed" doesn't mean safe; ask your vet.`
    : base;
}

function Recipe({ item, index }: { item: ContentItem; index: ContentIndex }) {
  const d = item.data as Data & {
    ingredients: {
      kind: string;
      ref_slug: string | null;
      name: string;
      amount: number;
      unit: string;
      note: string | null;
    }[];
    steps: string[];
    safety: RecipeSafety;
  };
  const s = d.safety;
  const badges = [
    s.ingestible
      ? { tone: "ok" as const, label: d.use === "drink" ? "Safe to drink" : "Safe to eat" }
      : { tone: "warning" as const, label: "Not for eating or drinking" },
    ...(s.pet_friendly
      ? [{ tone: "ok" as const, label: "Pet friendly" }]
      : [{ tone: "danger" as const, label: "Keep away from pets" }]),
    ...(s.pregnancy_caution_ingredients.length
      ? [{ tone: "warning" as const, label: "Pregnancy caution" }]
      : []),
  ];
  const petLines = [
    s.pet_warnings.cats.length ? `Cats: ${s.pet_warnings.cats.join(", ")}` : "",
    s.pet_warnings.dogs.length ? `Dogs: ${s.pet_warnings.dogs.join(", ")}` : "",
    s.pregnancy_caution_ingredients.length
      ? `Pregnancy: ${s.pregnancy_caution_ingredients.join(", ")}`
      : "",
  ].filter(Boolean);

  return (
    <>
      <Facts
        rows={[
          ["Type", cap(text(d.type).replace(/_/g, " "))],
          ["How it's used", cap(text(d.use).replace(/_/g, " "))],
        ]}
      />
      <SafetyLabel
        badges={badges}
        cautions={[...petLines, ...s.human_cautions]}
        footnote="Calculated from each ingredient's safety data."
      />
      <section aria-labelledby="ingredients">
        <h2 id="ingredients">Ingredients</h2>
        <ul className="ingredients">
          {d.ingredients.map((ing, i) => {
            const target = ing.ref_slug ? index.get(ing.kind, ing.ref_slug) : undefined;
            return (
              <li key={i}>
                <span className="ingredients__qty">
                  {ing.amount} {ing.unit}
                </span>{" "}
                {target ? (
                  <Link
                    to="/library/$category/$slug"
                    params={{ category: target.category, slug: target.slug }}
                  >
                    {target.name}
                  </Link>
                ) : (
                  ing.name
                )}
                {ing.note && <span className="hx-muted">, {ing.note}</span>}
              </li>
            );
          })}
        </ul>
      </section>
      <section aria-labelledby="method">
        <h2 id="method">Method</h2>
        <ol className="steps">
          {d.steps.map((step, i) => (
            <li key={i}>{step}</li>
          ))}
        </ol>
      </section>
      {text(d.notes) && <p className="hx-muted">{text(d.notes)}</p>}
      <Intents intents={d.intents} index={index} />
    </>
  );
}

function Sabbat({ item, index }: { item: ContentItem; index: ContentIndex }) {
  const settings = useSettings();
  const d = item.data;
  const now = Date.now();
  const next = settings
    ? index.sabbatDates
        .filter((s) => s.sabbat_slug === item.slug && s.hemisphere === settings.hemisphere)
        .map((s) => sabbatStart(s, settings.sabbatDateStyle))
        .find((start) => eventDate(start).getTime() >= now - 86_400_000)
    : undefined;
  const links = (category: "plant" | "crystal", slugs: unknown) =>
    words(slugs).map((slug, i) => {
      const target = index.get(category, slug);
      return (
        <span key={slug}>
          {i > 0 && ", "}
          {target ? (
            <Link to="/library/$category/$slug" params={{ category, slug }}>
              {target.name}
            </Link>
          ) : (
            slug
          )}
        </span>
      );
    });
  return (
    <>
      <p className="plate__lede">{text(d.description)}</p>
      {next && (
        <p className="sabbat-date">
          Next:{" "}
          {formatDay(eventDate(next), {
            weekday: "long",
            month: "long",
            day: "numeric",
            year: "numeric",
          })}
        </p>
      )}
      <Facts
        rows={[
          ["Say it", text(d.pronunciation)],
          ["Also known as", words(d.also_known_as).join(", ")],
          ["Themes", words(d.themes).join(", ")],
          ["Colors", words(d.colors).join(", ")],
          ["Plants", links("plant", d.plants)],
          ["Crystals", links("crystal", d.crystals)],
          ["Foods", words(d.foods).join(", ")],
        ]}
      />
      <section aria-labelledby="ways">
        <h2 id="ways">Ways to celebrate</h2>
        <ul>
          {words(d.activities).map((a) => (
            <li key={a}>{a}</li>
          ))}
        </ul>
      </section>
    </>
  );
}

/** Reference categories: show any simple fields as facts. */
function Generic({ data, index, summary }: { data: Data; index: ContentIndex; summary: string }) {
  const skip = new Set([
    "slug",
    "name",
    "intents",
    "order",
    "sort_order",
    "number",
    "description",
    "guidance",
  ]);
  const rows: [string, ReactNode][] = Object.entries(data)
    .filter(
      ([k, v]) =>
        !skip.has(k) &&
        v !== null &&
        (typeof v === "string" || typeof v === "number" || Array.isArray(v)),
    )
    .map(([k, v]) => [
      cap(k.replace(/_/g, " ")),
      Array.isArray(v) ? words(v).join(", ") : String(v),
    ]);
  return (
    <>
      <p className="plate__lede">{text(data.description) || text(data.guidance) || summary}</p>
      <Facts rows={rows} />
      <Intents intents={data.intents} index={index} />
    </>
  );
}

function Facts({ rows }: { rows: [string, ReactNode][] }) {
  const shown = rows.filter(([, v]) => v !== "" && !(Array.isArray(v) && v.length === 0));
  if (!shown.length) return null;
  return (
    <dl className="facts">
      {shown.map(([k, v]) => (
        <div key={k}>
          <dt>{k}</dt>
          <dd>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Intentions link to their correspondence entry when one exists. */
function Intents({ intents, index }: { intents: unknown; index: ContentIndex }) {
  const list = Array.isArray(intents) ? (intents as string[]) : [];
  if (!list.length) return null;
  return (
    <section aria-labelledby="intents" className="intents">
      <h2 id="intents">Used for</h2>
      <ul className="tag-list">
        {list.map((slug) => {
          const item = index.get("intent", slug);
          const label = item?.name ?? slug.replace(/_/g, " ");
          return (
            <li key={slug}>
              {item ? (
                <Link to="/library/$category/$slug" params={{ category: "intent", slug }}>
                  {label}
                </Link>
              ) : (
                label
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function NotesAbout({ category, slug }: { category: ContentCategory; slug: string }) {
  const { user } = useServices();
  const notes =
    useQuery({
      queryKey: ["user", "notesAbout", category, slug],
      queryFn: () => user.notesAbout({ category, slug }),
    }).data ?? [];
  if (!notes.length) return null;
  return (
    <section aria-labelledby="your-notes">
      <h2 id="your-notes">Your notes</h2>
      <ul className="label-list">
        {notes.map((n) => (
          <li key={n.id}>
            <Link className="label-row" to="/grimoire/notes/$id" params={{ id: n.id }}>
              <span className="label-row__name">{n.title}</span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
